'use client';

import { notFound, useRouter, useSearchParams } from 'next/navigation';
import { ProfileInfo } from './components/ProfileInfo';
import { ProfileGrid } from './components';
import { useProfileUIStore } from './store/profile-ui-store';
import { cn } from '@workspace/ui/lib/utils';
import { useProfile } from '@/src/hooks/use-profile';
import { IAchievement, IProfile, IProject, ITile } from 'types/src/types';
import { InfoDialog } from '@/src/components/ui/InfoDialog';
import { useEffect, useState } from 'react';
import { getSubscription } from '@/src/actions/stripe';
import deletePaymentRecord from '@/src/utils/deletePaymentRecord';
import { getAchievementsForProfile } from '@/src/actions/profile';

type Props = {
  profile: IProfile | null;
  initialTiles: ITile[] | null;
  unpublishedProjects?: IProject[];
};

export const ProfilePageContent = ({
  profile = null,
  initialTiles = null,
  unpublishedProjects,
}: Props) => {
  const { data: currentUser, isLoading } = useProfile();
  const [achievements, setAchievements] = useState<IAchievement[]>([]);
  const isMobileView = useProfileUIStore((state) => state.isMobileView);
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState<
    string | null
  >(null);
  const [currentDonationId, setCurrentDonationId] = useState<string | null>(
    null
  );
  const [dialogInfo, setDialogInfo] = useState({
    title: '',
    description: '',
    isBillable: false,
  });
  const paymentId = searchParams.get('payment_id') as string;

  const getLatestSubscription = async () => {
    if (profile?.stripeAccountId) {
      const latest = await getSubscription(profile?.id);
      setCurrentSubscriptionId(latest.stripeSubscriptionId);
    }
  };

  const getAchievements = async () => {
    const { data: achievements, error } = await getAchievementsForProfile(
      profile?.uniqueLink as string
    );

    setAchievements(achievements ?? []);
  };

  useEffect(() => {
    const subscribeParam = searchParams.get('subscribe');
    if (subscribeParam === 'success') {
      getLatestSubscription();
      setShowSubscribeDialog(true);
      setDialogInfo({
        title: 'Thank you for your subscription!',
        description: "Supporting your favorite creator's journey.",
        isBillable: true,
      });
    } else if (subscribeParam === 'cancel') {
      setShowSubscribeDialog(true);
      setDialogInfo({
        title: 'Your subscription wasn’t completed',
        description: 'If you change your mind, you can always subscribe later.',
        isBillable: false,
      });
    }
    const donateParam = searchParams.get('donate');

    if (donateParam === 'success' && paymentId) {
      setCurrentDonationId(paymentId);
      setShowSubscribeDialog(true);
      setDialogInfo({
        title: 'Thank you for your donation!',
        description: "Empowering your creator's journey.",
        isBillable: true,
      });
    } else if (donateParam === 'cancel') {
      setShowSubscribeDialog(true);
      deletePaymentRecord(paymentId);
      setDialogInfo({
        title: 'Looks like you canceled your donation.',
        description: 'If you change your mind, you can always donate later.',
        isBillable: false,
      });
    }
    getAchievements();
  }, [searchParams]);

  const removeSubscribeParam = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('subscribe');

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    router.replace(newUrl);
  };

  const handleCloseDialog = async () => {
    setShowSubscribeDialog(false);
    removeSubscribeParam();
    if (currentDonationId) {
      setCurrentDonationId(null);
    }
  };

  if (!profile) {
    notFound();
  }

  const isMyProfile = currentUser?.uniqueLink === profile.uniqueLink;

  return (
    <div
      className={cn('gap-4 w-full', {
        'md:grid lg:grid-cols-9 xl:grid-cols-12 md:gap-[53px]': !isMobileView,
      })}
    >
      <ProfileInfo
        profile={profile}
        isMyProfile={isMyProfile}
        achievements={achievements}
        currentUser={currentUser ?? null}
        loadingPage={isLoading}
      />
      <ProfileGrid
        profile={profile}
        isMyProfile={isMyProfile}
        initialTiles={initialTiles}
        unpublishedProjects={isMyProfile ? unpublishedProjects : []}
      />
      <InfoDialog
        title={dialogInfo.title}
        description={dialogInfo.description}
        isOpen={showSubscribeDialog}
        onOpenChange={setShowSubscribeDialog}
        onClose={handleCloseDialog}
        creatorStripeId={profile.stripeAccountId as string}
        subscriptionId={currentSubscriptionId ?? undefined}
        donationId={currentDonationId ?? undefined}
        isBillable={dialogInfo.isBillable}
      />
    </div>
  );
};
